import {
  useRecordLastUpdate,
  useSearchTableFromRoute,
} from '@aibees/elephant/hooks';
import { getPersonUpdatesData } from '@aibees/elephant/hooks/people/use-get-person-updates-data';
import { ROUTES } from '@aibees/elephant/router';
import { getPathWithId } from '@aibees/elephant/utils';
import { Icon, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Badge, Box, Stack, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { PeopleProfileDetailPopover } from '../people-profile-detail-popover';
import { QuickView } from '../quick-view';
import { SearchSocialMedia } from '../search-social-media';
import { SocialLink } from '../search-social-media-item';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleProfileProps as Props } from './types';

const BadgeIcon = memo(() => <Icon name="bell-ring-outline" size={12} />);
BadgeIcon.displayName = 'PeopleProfile.BadgeIcon';

const AvatarFallbackIcon = memo(() => (
  <Icon size={20} name="person-outline" color="grey.400" />
));
AvatarFallbackIcon.displayName = 'PeopleProfile.AvatarFallbackIcon';

const LastUpdateLabel = memo(() => {
  const { lastUpdatePeople } = useRecordLastUpdate();
  return (
    <Typography
      className="last-records-update"
      variant="body5"
      fontWeight={400}
      color="grey.500"
      sx={SX.lastUpdate}
    >
      Last Update: {lastUpdatePeople}
    </Typography>
  );
});
LastUpdateLabel.displayName = 'PeopleProfile.LastUpdateLabel';

const AVATAR_LAZY_LOAD_SLOT_PROPS = {
  img: { loading: 'lazy' as const },
} as const;

export const PeopleProfile = memo((props: Props) => {
  const {
    className,
    id,
    imageSrc,
    name,
    width = 'auto',
    data,
    showPopover,
    socialLink,
    avatarSize,
    nameProps,
  } = props;

  const table = useSearchTableFromRoute();
  // Defer updates computation until after first paint to reduce table render delay
  const [deferredUpdates, setDeferredUpdates] = useState<{
    hasAnyUpdates: boolean;
  } | null>(null);
  useEffect(() => {
    setDeferredUpdates({
      hasAnyUpdates: getPersonUpdatesData(data).hasAnyUpdates,
    });
  }, [data]);
  const hasAnyUpdates = deferredUpdates?.hasAnyUpdates ?? false;
  const badgeVisible = useMemo(() => !!table, [table]);

  const link = useMemo(() => getPathWithId(ROUTES.people, id), [id]);

  const { variant = 'body2', fontWeight = 600 } = nameProps ?? {};

  return (
    <Styled.Wrapper
      className={classnames('PeopleProfile', className)}
      width={width}
      sx={SX.root}
    >
      <Box>
        <Badge
          invisible={!hasAnyUpdates || !badgeVisible}
          color="primary"
          size={16}
          variant="soft"
          badgeContent={<BadgeIcon />}
        >
          <Avatar
            color="white"
            variant="rounded"
            size={avatarSize ?? 32}
            src={imageSrc ?? undefined}
            slotProps={AVATAR_LAZY_LOAD_SLOT_PROPS}
          >
            <AvatarFallbackIcon />
          </Avatar>
        </Badge>
      </Box>

      <Stack sx={SX.infoWrapper}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack direction="row" sx={SX.nameRow}>
            <TextEllipsis
              value={name}
              to={link}
              variant={variant}
              fontWeight={fontWeight}
              color="grey.700"
              w="100%"
            />
            {showPopover && <PeopleProfileDetailPopover data={data} />}
          </Stack>
          {table && <QuickView id={id} hasUpdate={hasAnyUpdates} />}
        </Stack>
        <Stack
          direction="row"
          gap={0.5}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <SearchSocialMedia
            socialLink={socialLink as SocialLink}
            hideLinkOutIcon={true}
          />
          {table && <LastUpdateLabel />}
        </Stack>
      </Stack>
    </Styled.Wrapper>
  );
});

PeopleProfile.displayName = 'PeopleProfile';
